import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { TrendingUp, Trophy } from "lucide-react";

/**
 * V188 — Switcher Leaderboard banner.
 *
 * Compact pill row showing this week's top competitor switch-overs:
 *   "This week: 🥇 Zendesk 47 · 🥈 Surfer 32 · 🥉 Semrush 28"
 *
 * Hides itself silently if there are fewer than 3 total switches
 * (avoids embarrassing empty-leaderboard moments).
 */
const COPY = {
  sk: {
    label: "Tento týždeň",
    cta: "Pozri všetkých",
    total: "e-shopov prešlo zo svojich starých nástrojov",
  },
  en: {
    label: "This week",
    cta: "See all",
    total: "stores moved off legacy tools",
  },
  cs: {
    label: "Tento týden",
    cta: "Zobrazit vše",
    total: "e-shopů přešlo ze svých starých nástrojů",
  },
  de: {
    label: "Diese Woche",
    cta: "Alle ansehen",
    total: "Shops wechselten von alten Tools",
  },
};

const MEDAL = ["🥇", "🥈", "🥉", "4.", "5."];

export default function SwitcherLeaderboardBanner() {
  const { language } = useLanguage();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/switcher-leaderboard/this-week`, { params: { limit: 3 } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data || data.total_switches < 3 || !data.top?.length) return null;

  const lang = ["sk", "en", "cs", "de"].includes((language || "").split("-")[0])
    ? (language || "").split("-")[0]
    : "en";
  const dict = COPY[lang] || COPY.en;

  return (
    <section
      className="bg-gradient-to-r from-violet-950 via-fuchsia-950 to-rose-950 border-y border-white/5 py-4 px-4 lg:px-6"
      data-testid="switcher-leaderboard-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap justify-center text-white">
        <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase font-extrabold text-amber-300">
          <Trophy className="w-3.5 h-3.5" /> {dict.label}
        </div>

        <div className="flex items-center gap-3 flex-wrap" data-testid="leaderboard-rows">
          {data.top.map((r, i) => (
            <span
              key={r.slug}
              className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs"
              data-testid={`lb-row-${r.slug}`}
            >
              <span className="text-base leading-none">{MEDAL[i] || "•"}</span>
              <a
                href={`/vs/${r.slug}`}
                className="font-bold text-white hover:text-amber-200 transition-colors"
              >
                {r.name}
              </a>
              <span className="text-emerald-300 font-extrabold tabular-nums">
                {r.redemptions}
              </span>
            </span>
          ))}
        </div>

        <div className="hidden sm:flex items-center gap-1 text-[11px] text-violet-200 font-semibold">
          <TrendingUp className="w-3.5 h-3.5" />
          <span className="tabular-nums font-extrabold text-white">{data.total_switches}</span>
          <span>{dict.total}</span>
        </div>
      </div>
    </section>
  );
}
